import React, { useEffect, useRef, useState } from "react";
import TanSelector from "../TanSelector/TanSelector";
import FilterStage from "../FilterStage/FilterStage";
import useCameraStream from "../../hooks/useCameraStream";
import useBodyPixMask from "../../hooks/useBodyPixMask";
import "./TanFilterVideo.css";

// Render at full RAF rate by default. If the effect looks jittery on a device,
// lower the WHOLE feed's frame rate here (e.g. 24) — this throttles the video
// draw and the body-pix mask together so they stay in sync.
const TARGET_FPS = 0; // 0 = uncapped (every frame)

const TanFilterVideo = ({ tanLevel = 1, circleClicked, showRapid, embedded = false }) => {
    const { videoRef, ready, dimensions } = useCameraStream();
    const bodyMask = useBodyPixMask({ ready });
    const canvasRef = useRef(null);
    const maskCanvasRef = useRef(null);
    const tanLevelRef = useRef(tanLevel);
    const splitMode = useRef(false);
    const [splitState, setSplitState] = useState(false);
    const [loading, setLoading] = useState(true);

    // Array of tan colors for the selector
    const circleColors = [
        "#e4dace", "#dbccc0", "#d2c2b3", "#cab7a6", "#bca48f", "#b59b85",
        "#af937b", "#a98b71", "#967457", "#8b6647", "#805938", "#764d2a"
    ];
    const tanDeltas = [
        { r: -1, g: -1, b: -2 },
        { r: -1, g: -2, b: -4 },
        { r: -2, g: -3, b: -5 },
        { r: -2, g: -4, b: -6 },
        { r: -3, g: -5, b: -7 },
        { r: -3, g: -6, b: -9 },
        { r: -4, g: -7, b: -10 },
        { r: -4, g: -8, b: -11 },
        { r: -5, g: -9, b: -12 },
        { r: -5, g: -10, b: -14 },
        { r: -6, g: -11, b: -15 },
        { r: -6, g: -12, b: -16 },
        { r: -6, g: -12, b: -17 },
    ];

    // Render loop starts once the camera is ready and we know the frame size.
    useEffect(() => {
        if (!ready || !canvasRef.current || !maskCanvasRef.current) return undefined;
        const video = videoRef.current;
        const canvas = canvasRef.current;
        const ctx = canvas.getContext("2d", { willReadFrequently: true });
        const maskCanvas = maskCanvasRef.current;
        const maskCtx = maskCanvas.getContext("2d", { willReadFrequently: true });

        maskCanvas.width = canvas.width = dimensions.width;
        maskCanvas.height = canvas.height = dimensions.height;
        setLoading(false);

        // Body-pix already isolates the person; this just keeps the tint on skin
        // (not clothes). Loosened from the original so more skin tones and
        // shadowed areas count as "body" — wider coverage, edges feathered below.
        const isSkinPixel = (r, g, b) => (
            r > 55 && g > 25 && b > 15 &&
            r >= g && r > b &&
            (r - g) >= 4 &&
            (Math.max(r, g, b) - Math.min(r, g, b)) > 8
        );

        const frameInterval = TARGET_FPS ? 1000 / TARGET_FPS : 0;
        let lastTs = 0;
        let raf;
        const renderLoop = (ts) => {
            raf = requestAnimationFrame(renderLoop);
            if (frameInterval && ts - lastTs < frameInterval) return;
            lastTs = ts;
            // Ask body-pix for a fresh person mask (runs every rendered frame).
            bodyMask.updateFromVideo(video);
            const m = bodyMask.getMask();
            const usePerson = !!(m && m.data && m.width === canvas.width && m.height === canvas.height);

            // Step 1: draw video + build the skin mask, confined to the person.
            ctx.drawImage(video, 0, 0);
            const frame = ctx.getImageData(0, 0, canvas.width, canvas.height);
            const maskImageData = maskCtx.createImageData(canvas.width, canvas.height);

            const strength = Math.min(Math.max(tanLevelRef.current, 1), 12);
            const { r: dr, g: dg, b: db } = tanDeltas[strength - 1];
            const multiplier = 3;

            for (let i = 0; i < frame.data.length; i += 4) {
                const x = (i / 4) % canvas.width;
                if (splitMode.current && x < canvas.width / 2) continue;
                // Suppress background: only consider pixels inside the person mask.
                const personAlpha = usePerson ? m.data[i >> 2] : 1;
                if (personAlpha <= 0.5) continue;
                if (isSkinPixel(frame.data[i], frame.data[i + 1], frame.data[i + 2])) {
                    maskImageData.data[i + 3] = 255;
                }
            }

            // Step 2: blur the mask for soft edges.
            maskCtx.putImageData(maskImageData, 0, 0);
            maskCtx.globalCompositeOperation = "copy";
            maskCtx.filter = "blur(" + canvas.width / 45 + "px)";
            maskCtx.drawImage(maskCanvas, 0, 0);
            maskCtx.filter = "none";

            // Step 3: apply the tint based on the blurred alpha.
            const blurredMask = maskCtx.getImageData(0, 0, canvas.width, canvas.height);
            for (let i = 0; i < frame.data.length; i += 4) {
                const alpha = blurredMask.data[i + 3] / 255;
                if (alpha === 0) continue;
                const x = (i / 4) % canvas.width;
                if (splitMode.current && x < canvas.width / 2) continue;
                frame.data[i] = Math.max(0, Math.min(255, frame.data[i] + dr * multiplier * alpha));
                frame.data[i + 1] = Math.max(0, Math.min(255, frame.data[i + 1] + dg * multiplier * alpha));
                frame.data[i + 2] = Math.max(0, Math.min(255, frame.data[i + 2] + db * multiplier * alpha));
            }

            ctx.putImageData(frame, 0, 0);
        };

        raf = requestAnimationFrame(renderLoop);
        return () => {
            if (raf) cancelAnimationFrame(raf);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ready, dimensions.width, dimensions.height]);

    const toggleSplit = () => {
        const newMode = !splitMode.current;
        splitMode.current = newMode;
        setSplitState(newMode);
    };

    const cameraNode = (
        <>
            {splitState && <div className="tan-filter__split" />}
            <video ref={videoRef} className="tan-filter__hidden" playsInline muted />
            <canvas ref={maskCanvasRef} className="tan-filter__hidden" />
            <canvas ref={canvasRef} className="tan-filter__canvas" />
        </>
    );

    return (
        <FilterStage
            fullBleed={!embedded}
            controlsPlacement={embedded ? "overlay" : "auto"}
            busy={loading}
            onSurfaceClick={toggleSplit}
            camera={cameraNode}
            controls={!loading && (
                <TanSelector
                    circleColors={circleColors}
                    selectedLevel={tanLevelRef.current}
                    setSelectedLevel={(level) => {
                        tanLevelRef.current = level;
                        if (circleClicked) circleClicked(level);
                    }}
                    showRapid={showRapid}
                    layout={embedded ? "horizontal" : "auto"}
                />
            )}
        />
    );
};

export default TanFilterVideo;
